import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { assignRoleToUser, removeRoleFromUser } from '@adornis/users/server/roles.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { TZDate } from '@date-fns/tz';
import { defer, switchMap, type Observable } from 'rxjs';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Company } from '../db/company.js';
import { MatchingWeekRole } from '../db/enums/roles.js';
import { JobApplication } from '../db/job-application.js';
import { MWUserData } from '../db/user.js';
import { CompanyUserAddedMail } from '../mails/company-mails.js';
import { activeYearStart, berlinYearStart } from '../shared/account-status.js';
import { normalizeSingleEmail } from '../shared/email.js';
import { assertCallerIsAdmin } from './admin-guard.js';

// ─── List all users (paginated + filterable) ───

// The user collection is shared with the ICSRM Buchungsheld deployment, so the
// list must be scoped to users that actually exist in matching week: candidates
// with onboarding data, company members, or holders of a matching-week role.
// Without this, Buchungsheld-only users show up and their detail pages lead nowhere.
const matchingWeekUsersOnlyStages = [
  { $lookup: { from: MWUserData._collectionName, localField: '_id', foreignField: 'userID', as: '_mwUserData' } },
  { $lookup: { from: Company._collectionName, localField: '_id', foreignField: 'userIDs', as: '_mwCompanies' } },
  {
    $match: {
      $or: [
        { '_mwUserData.0': { $exists: true } },
        { '_mwCompanies.0': { $exists: true } },
        { 'roles.name': { $regex: '^mw:' } },
      ],
    },
  },
  { $unset: ['_mwUserData', '_mwCompanies'] },
];

/**
 * Optional post-lookup stages that narrow the user list by their job applications.
 * These can't go through the AdornisFilter because that is applied as the initial
 * `$match` on the users collection (before any lookup), so they are appended as
 * aggregation stages instead. `applicationStatus` matches users with at least one
 * application in that status; `interviewOutcome` matches on the confirmed attendance
 * (or interviews still awaiting confirmation).
 */
const applicationFilterStages = (
  applicationStatus?: Maybe<string>,
  interviewOutcome?: Maybe<string>,
  now = new Date(),
) => {
  if (!applicationStatus && !interviewOutcome) return [];

  const matches: Record<string, any>[] = [];
  if (applicationStatus) matches.push({ '_apps.status': applicationStatus });
  if (interviewOutcome === 'ATTENDED') matches.push({ '_apps.interviewAttended': true });
  else if (interviewOutcome === 'NOT_ATTENDED') matches.push({ '_apps.interviewAttended': false });
  else if (interviewOutcome === 'PENDING')
    matches.push({
      // booked, the slot is in the past, and no attendance recorded yet
      _apps: { $elemMatch: { status: 'BOOKED', interviewStart: { $lt: now }, interviewAttended: null } },
    });

  if (!matches.length) return [];
  return [
    { $lookup: { from: JobApplication._collectionName, localField: '_id', foreignField: 'userId', as: '_apps' } },
    { $match: matches.length === 1 ? matches[0] : { $and: matches } },
    { $unset: '_apps' },
  ];
};

const accountStatusFilterStages = (createdYear?: Maybe<number>, activeOnly?: Maybe<boolean>, now = new Date()) => {
  const matches: object[] = [];
  if (createdYear !== null && createdYear !== undefined) {
    matches.push({
      createdAt: {
        $gte: berlinYearStart(createdYear),
        $lt: berlinYearStart(createdYear + 1),
      },
    });
  }
  if (activeOnly) {
    const start = activeYearStart(now);
    matches.push({ $or: [{ createdAt: { $gte: start } }, { lastActiveAt: { $gte: start } }] });
  }
  if (!matches.length) return [];
  return [{ $match: matches.length === 1 ? matches[0] : { $and: matches } }];
};

export const buildUserListStages = (
  args: {
    applicationStatus?: Maybe<string>;
    interviewOutcome?: Maybe<string>;
    createdYear?: Maybe<number>;
    activeOnly?: Maybe<boolean>;
  },
  now = new Date(),
): object[] => [
  ...accountStatusFilterStages(args.createdYear, args.activeOnly, now),
  ...matchingWeekUsersOnlyStages,
  ...applicationFilterStages(args.applicationStatus, args.interviewOutcome, now),
];

export const getUserCreatedYears = registerQuery({
  operationName: 'getUserCreatedYears',
  type: () => [Int],
  params: [],
  resolve: () => async () => {
    await assertCallerIsAdmin();
    const years = await mongo()
      .collection(User._collectionName)
      .aggregate([
        ...matchingWeekUsersOnlyStages,
        { $match: { createdAt: { $type: 'date' } } },
        { $group: { _id: { $year: { date: '$createdAt', timezone: 'Europe/Berlin' } } } },
      ])
      .toArray();
    return [
      ...new Set([new TZDate(new Date(), 'Europe/Berlin').getFullYear(), ...years.map(year => year._id as number)]),
    ].sort((a, b) => b - a);
  },
});

export const countAllUsers = registerQuery({
  operationName: 'countAllUsers',
  type: () => Int,
  params: [
    { name: 'filter', type: () => AdornisFilter },
    { name: 'applicationStatus', type: () => String },
    { name: 'interviewOutcome', type: () => String },
    { name: 'createdYear', type: () => Int },
    { name: 'activeOnly', type: () => Boolean },
  ],
  resolve:
    (
      filter: AdornisFilter,
      applicationStatus?: string,
      interviewOutcome?: string,
      createdYear?: number,
      activeOnly?: boolean,
    ) =>
    async () => {
      await assertCallerIsAdmin();
      const [result] = await mongo()
        .collection(User._collectionName)
        .aggregate([
          { $match: filter.getMongoFilter() },
          ...buildUserListStages({ applicationStatus, interviewOutcome, createdYear, activeOnly }),
          { $count: 'count' },
        ])
        .toArray();
      return result?.count ?? 0;
    },
});

export const subscribeAllUsers = registerSubscription({
  operationName: 'subscribeAllUsers',
  type: () => [User],
  resolve:
    (
      skip: number,
      limit: number,
      filter: AdornisFilter,
      applicationStatus?: string,
      interviewOutcome?: string,
      createdYear?: number,
      activeOnly?: boolean,
    ) =>
    <SelSet extends BaseQLSelectionSet<User>>(_gqlFields: SelSet) => {
      // Admin-only — gate inside the observable so every subscriber re-checks.
      return defer(() => assertCallerIsAdmin()).pipe(
        switchMap(() => {
          const sort = filter.getMongoOptions().sort;
          return subscribePaginatedAggregation<typeof User>(
            User,
            skip,
            limit,
            filter.getMongoFilter(),
            [
              ...buildUserListStages({ applicationStatus, interviewOutcome, createdYear, activeOnly }),
              { $sort: Object.keys(sort ?? {}).length ? { ...sort, _id: 1 } : { email: 1 } },
            ],
            {},
          );
        }),
      ) as Observable<NarrowedEntity<User, SelSet>[]>;
    },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'applicationStatus', type: () => String },
    { name: 'interviewOutcome', type: () => String },
    { name: 'createdYear', type: () => Int },
    { name: 'activeOnly', type: () => Boolean },
  ],
});

// ─── Toggle the matching-week admin role ───

export const adminToggleAdminRole = registerMutation({
  operationName: 'adminToggleAdminRole',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => ID },
    { name: 'shouldBeAdmin', type: () => Boolean },
  ],
  resolve: (userID: string, shouldBeAdmin: boolean) => async () => {
    await assertCallerIsAdmin();
    if (!shouldBeAdmin && !context.serverContext && userID === context.userID) {
      throw new Error('You cannot remove the admin role from yourself');
    }
    if (shouldBeAdmin) {
      await assignRoleToUser(userID, MatchingWeekRole.ADMIN, [GLOBAL_CONTEXT], User as any);
    } else {
      await removeRoleFromUser(userID, MatchingWeekRole.ADMIN, [GLOBAL_CONTEXT], User as any);
    }
    return true;
  },
});

// ─── Create a user, optionally with a role ───

export const adminCreateUser = registerMutation({
  operationName: 'adminCreateUser',
  type: () => ID,
  params: [
    { name: 'email', type: () => String },
    { name: 'password', type: () => String },
    { name: 'firstName', type: () => String },
    { name: 'lastName', type: () => String },
    { name: 'role', type: () => String },
    { name: 'companyID', type: () => ID },
  ],
  resolve:
    (email: string, password: string, firstName: string, lastName: string, role: string, companyID: string) =>
    async () => {
      await assertCallerIsAdmin();
      const trimmedEmail = normalizeSingleEmail(email);
      const userCol = mongo().collection(User._collectionName);
      const existing = await userCol.findOne({
        email: { $regex: `^${trimmedEmail.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' },
      });
      if (existing) throw new Error('Email already in use');

      const newUser = new User({
        email: trimmedEmail,
        username: trimmedEmail,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      } as any);
      const userID = await createNewUser(newUser as any, password.trim() || (undefined as any), false);

      if (role === MatchingWeekRole.ADMIN) {
        await assignRoleToUser(userID, MatchingWeekRole.ADMIN, [GLOBAL_CONTEXT], User as any);
      } else if (role === MatchingWeekRole.COMPANY) {
        if (!companyID) throw new Error('companyID is required when assigning the company role');
        const companyCol = await scopedCollection(Company);
        const result = await companyCol.updateOne({ _id: companyID } as any, { $addToSet: { userIDs: userID } });
        if (!result.matchedCount) throw new Error('Company not found');
        // role auto-assigned by listenToChangesAndUpdateRolesOfUsers watcher

        // Welcome mail — `newUser` carries the firstName/lastName we just
        // persisted, so the greeting renders with the real name. Failure to
        // send is logged but doesn't block account creation.
        try {
          const _mailer = await mailer;
          if (_mailer) {
            await CompanyUserAddedMail(newUser, A.absoluteUrl('/company')).send({
              mailer: _mailer,
              recipients: [new Recipient(trimmedEmail)],
            });
          }
        } catch (e) {
          logger.error({ err: e, userID, companyID }, 'Failed to send CompanyUserAddedMail');
        }
      }
      return userID;
    },
});

// ─── Add an existing user to a company (admin-gated) ───

export const adminAddUserToCompany = registerMutation({
  operationName: 'adminAddUserToCompany',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => ID },
    { name: 'companyID', type: () => ID },
  ],
  resolve: (userID: string, companyID: string) => async () => {
    await assertCallerIsAdmin();
    const userResult = await mongo()
      .collection<EntityData<User>>(User._collectionName)
      .findOne({ _id: userID } as any, { projection: { email: 1, firstName: 1, lastName: 1, username: 1 } });
    if (!userResult) throw new Error('User not found');

    const companyCol = await scopedCollection(Company);
    const result = await companyCol.updateOne({ _id: companyID } as any, { $addToSet: { userIDs: userID } });
    if (!result.matchedCount) throw new Error('Company not found');

    // role auto-assigned by listenToChangesAndUpdateRolesOfUsers watcher
    if (result.modifiedCount && userResult.email) {
      try {
        const _mailer = await mailer;
        if (_mailer) {
          await CompanyUserAddedMail(new User(userResult), A.absoluteUrl('/company')).send({
            mailer: _mailer,
            recipients: [new Recipient(userResult.email as string)],
          });
        }
      } catch (e) {
        logger.error({ err: e }, 'Failed to send company-user-added mail');
      }
    }
    return true;
  },
});

// ─── Remove a user from a company (and the corresponding company role) ───

export const adminRemoveUserFromCompany = registerMutation({
  operationName: 'adminRemoveUserFromCompany',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => ID },
    { name: 'companyID', type: () => ID },
  ],
  resolve: (userID: string, companyID: string) => async () => {
    await assertCallerIsAdmin();
    const companyCol = await scopedCollection(Company);
    await companyCol.updateOne({ _id: companyID } as any, { $pull: { userIDs: userID } } as any);
    // The role-watcher only ADDS roles; remove the company-context role manually.
    await removeRoleFromUser(userID, MatchingWeekRole.COMPANY, [companyID], User as any);
    return true;
  },
});

// ─── Companies a user belongs to (read-only, for the user-detail page) ───

export const getCompaniesForUser = registerQuery({
  operationName: 'getCompaniesForUser',
  type: () => [Company],
  params: [{ name: 'userID', type: () => ID }],
  resolve:
    (userID: string) =>
    async <SelSet extends BaseQLSelectionSet<Company>>(_gqlFields: SelSet) => {
      await assertCallerIsAdmin();
      const col = mongo().collection<EntityData<Company>>(Company._collectionName);
      const docs = await col.find({ userIDs: userID } as any).toArray();
      return docs as unknown as Array<NarrowedEntity<Company, SelSet>>;
    },
});
